import React from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonContinue } from '../../../common/Button/ButtonContinue';
import TextInput from '../../../common/TextInput/TextInput';
import { useContext, useEffect, useState } from 'react';
import { ButtonBack } from '../../../common/Button/ButtonBack';
import { setCountryCode } from '../../../common/HelperMethods';
import { PageAbsolutePath } from '../../../../consts/Common';
import { SiteContext } from '../../../../context/SiteContext';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { CarInsuranceApi } from '../../../../api/CarInsuranceApi';
import {
  DealerLoginResponseModel,
  InsuranceJourneyContext
} from '../../../common/CommonTypes';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

const Login = () => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const { site, setSite } = useContext(SiteContext);
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { dealerLoginPage } = pageEvents;

  const {
    setDealerHomepageOptions,
    setDealerUsername,
    setDealerRepresentative
  } = dealerLoginPage;

  const { tiaProductCode } = journey;

  const { brand } = site;

  const phoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const onChangeUsername = (e: React.FormEvent<HTMLInputElement>) => {
    setUsername(e.currentTarget.value);
  };

  const onChangePassword = (e: React.FormEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
  };

  const onChangeRepresentativeName = (e: React.FormEvent<HTMLInputElement>) => {
    setRepresentativeName(e.currentTarget.value);
  };

  const onClickDealerLogin = () => {
    if (
      CommonValidation.isEmpty(username) ||
      CommonValidation.isEmpty(password)
    ) {
      setErrorMessage('Login failed.');
    } else {
      setErrorMessage('');
      setLoading(true);
      authenticateDealer();
    }
  };

  const authenticateDealer = async () => {
    const controller = new AbortController();

    const dealerLoginSuccess = (
      dealerLoginResponse: DealerLoginResponseModel
    ) => {
      setLoading(false);
      if (dealerLoginResponse.dealerCredentialResponse) {
        if (dealerLoginResponse.dealerCredentialResponse.isDealer) {
          const updateUser = {
            ...site.user,
            token: dealerLoginResponse.dealerCredentialResponse.token,
            isDealer: dealerLoginResponse.dealerCredentialResponse.isDealer,
            insuranceDealerName:
              dealerLoginResponse.dealerCredentialResponse.name,
            insuranceDealershipName:
              dealerLoginResponse.dealerCredentialResponse.dealershipName,
            insuranceDealerLoginUserName: username,
            insuranceDealershipLocation:
              dealerLoginResponse.dealerCredentialResponse
                .insuranceDealershipLocation ?? '',
            insuranceDealerRepresentative: representativeName ?? '',
            insuranceDealerId:
              dealerLoginResponse.dealerCredentialResponse.dealerId,
            insuranceDealerCode:
              dealerLoginResponse.dealerCredentialResponse.dealerCode ?? ''
          };
          setSite({
            ...site,
            user: updateUser
          });

          setDealerHomepageOptions(dealerLoginResponse.dealerHomeOptions);
          setDealerUsername(username);
          setDealerRepresentative(representativeName);

          setErrorMessage('');
        } else {
          setErrorMessage('Login failed.');
        }
      } else {
        setErrorMessage('Login failed.');
      }
    };

    await new CarInsuranceApi().AuthenticateDealer(
      username,
      password,
      representativeName,
      tiaProductCode,
      dealerLoginSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onBackButtonClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.DealerLandingPage,
      site.countryCode
    );
    navigate(path);
  };

  useEffect(() => {
    if (
      site.user.isDealer &&
      CommonValidation.isNotEmpty(site.user.insuranceDealerName)
    ) {
      navigate(PageAbsolutePath.Insurance.DealersHomePage);
    }
    console.log(site.user);
  }, [site.user]);

  return (
    <div className='login-form'>
      <h4 className='section-header m3-top'>Dealer login</h4>

      <div className='one-col'>
        {errorMessage && <p className='error'>{errorMessage}</p>}
        <div className='control-label'>Username</div>
        <div className='control'>
          <TextInput
            placeholder={''}
            name='username'
            className='input-text w250'
            value={username}
            onChange={onChangeUsername}
            error={''}
            readOnly={false}
            hasError={errorMessage !== '' && username === ''}
          />
        </div>

        <div className='control-label'>Password</div>
        <div className='control'>
          <TextInput
            placeholder={''}
            name='password'
            className='input-text w250'
            value={password}
            onChange={onChangePassword}
            error={''}
            readOnly={false}
            inputType='password'
            hasError={errorMessage !== '' && password === ''}
          />
        </div>

        <div className='control-label'>Dealer representative full name</div>
        <div className='control'>
          <TextInput
            placeholder={''}
            name='representativeName'
            className='input-text w250'
            value={representativeName}
            onChange={onChangeRepresentativeName}
            error={''}
            readOnly={false}
            hasError={errorMessage !== '' && representativeName === ''}
          />
        </div>
      </div>
      <div className='two-col'>
        <div className='even-space-out align-with-form'>
          <div className='login-need-help'>
            <h6>Need Help?</h6>
            <a className='calltext' href={`tel:${phoneNumber}`}>
              {phoneNumber}
            </a>
          </div>
          <div className='mt-3'>
            <LoadingContainer loading={loading} message={'Please wait...'}>
              <ButtonContinue
                name='btnDealerLogin'
                onClick={onClickDealerLogin}
                text='Login'
                additionalClassNames='btn-ok'
              />
            </LoadingContainer>
          </div>
        </div>
      </div>
      <br />

      <h6>
        To understand how we may process information read our{' '}
        <Link to={'#'}>Fair Use Notice</Link>
      </h6>

      <h4 className='section-header m3-top'>Confirmation</h4>

      <p>
        As an approved user of this driveaway insurance system, I agree to the
        following:
      </p>

      <p>
        1. I confirm that the customer/applicant(s) has given their consent for
        information to be entered on their behalf.
      </p>

      <p>
        2. The customer has been made aware of the need to take all reasonable
        care in answering all questions in relation to this insurance honestly
        and to the best of their knowledge. Their failure to take reasonable
        care in avoiding misrepresentation in relation to the information
        provided could result in their policy being cancelled or their claim
        being rejected or not fully paid.
      </p>

      <p>
        3. I understand that it is a requirement to only enter 100% accurate
        customer contact information and that repeated use of incorrect email
        addresses and telephone numbers will result in this facility being
        withdrawn.
      </p>

      <p>
        4. You have seen the Driving Licence of the customer prior to setting up
        any insurance.
      </p>

      <p>
        5. I will provide the customer with the cover note and ensure they are
        made aware that they will need to provide a signed copy in the event
        that they make a claim.
      </p>

      <p>
        6. I understand that I am required to ensure the customer signs the
        cover note to confirm all details are correct before they are allowed to
        drive the vehicle.
      </p>

      <p>
        7. I will ensure that a copy of the signed cover note is retained and
        filed appropriately at the dealership and available on request for
        auditing purposes.
      </p>

      <div className='flex-row'>
        <ButtonBack
          name='btnBack'
          text='Back'
          onClick={onBackButtonClick}
          additionalClassNames='btn-back'
        />
      </div>
    </div>
  );
};

export default Login;
